import { useEffect, useState, type FormEvent } from 'react';
import { UNIT_TYPES, discountPct, formatQty, lineGross, parseQty, percentOf, type UnitType } from '@pos/core';
import { Alert, Button, Field, Input, cx } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { MoneyInput } from '../../ui/MoneyInput.tsx';
import { cartTotals, removeLine, setLineDiscount, setLineQty, type Cart, type CartLine } from './cart.ts';

/** Texto de cantidad para un campo: 1500 → "1,5". */
const qtyText = (milli: number) => String(milli / 1000).replace('.', ',');

// ---------------------------------------------------------------------------
// Cantidad al agregar un producto pesable (o por litro / metro)
// ---------------------------------------------------------------------------

export function QtyDialog({
  product,
  onClose,
  onConfirm,
  money,
}: {
  product: { label: string; unitType: UnitType; price: number } | null;
  onClose: () => void;
  onConfirm: (qtyMilli: number) => void;
  money: (c: number) => string;
}) {
  const [text, setText] = useState('');
  useEffect(() => setText(''), [product]);
  if (!product) return <Dialog open={false} onClose={onClose} title="" children={null} />;
  const unit = UNIT_TYPES[product.unitType];
  const qty = text.trim() ? parseQty(text, product.unitType) : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (qty && qty > 0) onConfirm(qty);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={product.label}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="qty-form" disabled={!qty}>
            Agregar
          </Button>
        </>
      }
    >
      <form id="qty-form" onSubmit={submit} className="space-y-3">
        <Field label={`Cantidad (${unit.symbol})`} hint={`${money(product.price)} por ${unit.symbol}. Ej.: 0,250 = 250 g.`}>
          {(id) => <Input id={id} inputMode="decimal" autoFocus value={text} invalid={!!text.trim() && !qty} onChange={(e) => setText(e.target.value)} />}
        </Field>
        {qty ? <p className="text-lg font-semibold">{money(lineGross(product.price, qty))}</p> : null}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Editar una línea del carrito: cantidad, descuento, quitar
// ---------------------------------------------------------------------------

export function LineDialog({
  cart,
  index,
  maxDiscount,
  onClose,
  onChange,
  money,
}: {
  cart: Cart;
  index: number | null;
  maxDiscount: number;
  onClose: () => void;
  onChange: (cart: Cart) => void;
  money: (c: number) => string;
}) {
  const line: CartLine | undefined = index === null ? undefined : cart.lines[index];
  const [qty, setQty] = useState('');
  const [mode, setMode] = useState<'pct' | 'amount'>('pct');
  const [pct, setPct] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  // Los datos de la línea se cargan en el mismo render en que se abre (no en un efecto después):
  // así la cantidad ya está escrita cuando el campo toma el foco y queda marcada para reemplazarla.
  const [loaded, setLoaded] = useState<number | null>(null);
  if (index !== loaded) {
    setLoaded(index);
    if (line) {
      setQty(qtyText(line.qtyMilli));
      setMode('amount');
      setAmount(line.discount || null);
      setPct('');
    }
  }

  if (!line || index === null) return <Dialog open={false} onClose={onClose} title="" children={null} />;

  const qtyMilli = parseQty(qty, line.unitType);
  const gross = qtyMilli ? lineGross(line.unitPrice, qtyMilli) : 0;
  const pctValue = Number(pct.replace(',', '.'));
  const discount = mode === 'pct' ? (pct.trim() && Number.isFinite(pctValue) ? percentOf(gross, pctValue) : 0) : (amount ?? 0);
  let error: string | null = null;
  let next: Cart | null = null;
  if (!qtyMilli) error = 'Cantidad inválida.';
  else if (discount < 0 || discount > gross) error = 'El descuento no puede superar el importe.';
  else {
    next = setLineDiscount(setLineQty(cart, index, qtyMilli), index, discount);
    const t = cartTotals(next);
    if (t.discountTotal > 0 && discountPct(t.subtotal, t.discountTotal) > maxDiscount + 1e-9) {
      error = maxDiscount === 0 ? 'No tenés permiso para hacer descuentos.' : `Con este descuento la venta supera tu tope de ${maxDiscount} %.`;
    }
  }

  const step = (delta: number) => {
    const current = parseQty(qty, line.unitType) ?? 0;
    setQty(qtyText(Math.max(1000, current + delta * 1000)));
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!next || error) return;
    onChange(next);
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={line.label}
      footer={
        <>
          <Button
            variant="dangerGhost"
            onClick={() => {
              onChange(removeLine(cart, index));
              onClose();
            }}
          >
            Quitar
          </Button>
          <Button type="submit" form="line-form" disabled={!!error || !next}>
            Aplicar
          </Button>
        </>
      }
    >
      <form id="line-form" onSubmit={submit} className="space-y-4">
        <Field label={`Cantidad${line.unitType === 'unit' ? '' : ` (${UNIT_TYPES[line.unitType].symbol})`}`} hint={`${money(line.unitPrice)} c/u`}>
          {(id) => (
            <div className="flex items-center gap-2">
              {line.unitType === 'unit' && (
                <Button variant="secondary" onClick={() => step(-1)} aria-label="Restar uno">
                  −
                </Button>
              )}
              <Input
                id={id}
                inputMode="decimal"
                autoFocus
                value={qty}
                invalid={!qtyMilli}
                onChange={(e) => setQty(e.target.value)}
                // Queda marcada para escribir encima la cantidad nueva; las flechas suman o restan uno.
                onFocus={(e) => e.currentTarget.select()}
                onKeyDown={(e) => {
                  if (line.unitType !== 'unit' || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
                  e.preventDefault();
                  step(e.key === 'ArrowUp' ? 1 : -1);
                }}
                className="text-center"
              />
              {line.unitType === 'unit' && (
                <Button variant="secondary" onClick={() => step(1)} aria-label="Sumar uno">
                  +
                </Button>
              )}
            </div>
          )}
        </Field>
        {maxDiscount > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Descuento en este producto</span>
              <ModeSwitch mode={mode} onChange={setMode} />
            </div>
            {mode === 'pct' ? (
              <Input inputMode="decimal" placeholder="Ej.: 10" value={pct} onChange={(e) => setPct(e.target.value)} aria-label="Descuento en %" />
            ) : (
              <MoneyInput value={amount} onChange={setAmount} aria-label="Descuento en pesos" />
            )}
          </div>
        )}
        <div className="flex items-baseline justify-between border-t border-slate-200 pt-3">
          <span className="text-sm text-slate-600">{qtyMilli ? `${formatQty(qtyMilli, line.unitType)} × ${money(line.unitPrice)}` : '—'}</span>
          <span className="text-lg font-semibold">{money(Math.max(0, gross - discount))}</span>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
      </form>
    </Dialog>
  );
}

function ModeSwitch({ mode, onChange }: { mode: 'pct' | 'amount'; onChange: (m: 'pct' | 'amount') => void }) {
  return (
    <div className="flex rounded-lg bg-slate-100 p-0.5 text-sm" role="radiogroup">
      {(['pct', 'amount'] as const).map((m) => (
        <button
          key={m}
          type="button"
          role="radio"
          aria-checked={mode === m}
          onClick={() => onChange(m)}
          className={cx('rounded-md px-3 py-1 font-medium', mode === m ? 'bg-white text-slate-900 shadow-card' : 'text-slate-600')}
        >
          {m === 'pct' ? '%' : '$'}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Descuento sobre el total
// ---------------------------------------------------------------------------

export function DiscountDialog({
  open,
  cart,
  maxDiscount,
  onClose,
  onChange,
  money,
}: {
  open: boolean;
  cart: Cart;
  maxDiscount: number;
  onClose: () => void;
  onChange: (cart: Cart) => void;
  money: (c: number) => string;
}) {
  const [mode, setMode] = useState<'pct' | 'amount'>('pct');
  const [pct, setPct] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  useEffect(() => {
    if (!open) return;
    setMode(cart.globalDiscount ? 'amount' : 'pct');
    setAmount(cart.globalDiscount || null);
    setPct('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return <Dialog open={false} onClose={onClose} title="" children={null} />;
  const base = cartTotals({ ...cart, globalDiscount: 0 });
  const available = base.baseTotal;
  const pctValue = Number(pct.replace(',', '.'));
  const value = mode === 'pct' ? (pct.trim() && Number.isFinite(pctValue) ? percentOf(available, pctValue) : 0) : (amount ?? 0);
  let error: string | null = null;
  if (value < 0 || value > available) error = 'El descuento no puede superar el total.';
  else if (value > 0 && discountPct(base.subtotal, base.discountTotal + value) > maxDiscount + 1e-9) {
    error = `Supera tu tope de descuento (${maxDiscount} %).`;
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (error) return;
    onChange({ ...cart, globalDiscount: value });
    onClose();
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Descuento sobre el total"
      footer={
        <>
          {cart.globalDiscount > 0 && (
            <Button
              variant="dangerGhost"
              onClick={() => {
                onChange({ ...cart, globalDiscount: 0 });
                onClose();
              }}
            >
              Sacar descuento
            </Button>
          )}
          <Button type="submit" form="discount-form" disabled={!!error}>
            Aplicar
          </Button>
        </>
      }
    >
      <form id="discount-form" onSubmit={submit} className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Total sin este descuento: {money(available)}</span>
          <ModeSwitch mode={mode} onChange={setMode} />
        </div>
        {mode === 'pct' ? (
          <Input inputMode="decimal" autoFocus placeholder="Ej.: 10" value={pct} onChange={(e) => setPct(e.target.value)} aria-label="Descuento en %" />
        ) : (
          <MoneyInput value={amount} onChange={setAmount} aria-label="Descuento en pesos" />
        )}
        <p className="text-lg font-semibold">Total: {money(Math.max(0, available - value))}</p>
        {maxDiscount < 100 && <p className="text-sm text-slate-500">Tu tope de descuento es {maxDiscount} % por venta.</p>}
        {error && <Alert tone="error">{error}</Alert>}
      </form>
    </Dialog>
  );
}
